import { useState } from 'react';
import { useAppDispatch } from '../../useAppDispatch';
import { salesApi, useUpdateSubscriptionMutation } from '../service';
import { refreshAccessToken } from '../../store/iam/operations';
import { notification } from 'antd';

/**
 * Nothing here is a purchase. The customer already has a subscription and the card the provider
 * holds is what pays for it, so a plan change - including restarting a canceled one - never asks
 * for card details.
 *
 * Each failure says something different, because the customer's next step differs. Telling someone
 * to try again when trying again cannot work is worse than saying nothing.
 */
const SUBSCRIPTION_IS_GONE = 'Your subscription has ended. Choose a plan to start again.';
const PROVIDER_UNREACHABLE = 'The payment provider is not reachable right now. Please try again in a few minutes.';
const UPDATE_FAILED = 'We cannot update your subscription. Please try again or contact support!';

export default function useUpdateSubscription() {
    const dispatch = useAppDispatch();
    const [updatedPlanCode, setUpdatedPlanCode] = useState<string>('');

    const [updateSubscriptionTrigger, { isLoading: isUpdatingSubscription }] = useUpdateSubscriptionMutation();

    const reportFailure = (description: string) => {
        notification.error({ message: 'Error', description });
    };

    /** Re-read the subscription so the page stops offering an action that cannot work. */
    const refreshSubscription = () => {
        dispatch(salesApi.util.invalidateTags(['Subscription']));
    };

    const updateSubscription = async (planCode: string) => {
        setUpdatedPlanCode(planCode);

        try {
            const response = await updateSubscriptionTrigger({ plan_code: planCode }).unwrap();

            // An answer carrying an error is the provider refusing the change itself - a plan that is
            // no longer for sale, or a subscription it will not restart. Its own words are the useful
            // ones here, and the subscription may not be what this page thinks it is any more.
            if ('error' in response) {
                reportFailure(response.error || UPDATE_FAILED);
                refreshSubscription();
                return;
            }

            await dispatch(refreshAccessToken()); // TODO Refactor this after IAM
        } catch (error: any) {
            const status = error?.status;

            // 404: there is no subscription left to change. The paid period can end between loading
            // this page and pressing the button, and then buying again is the only way back.
            if (status === 404) {
                reportFailure(SUBSCRIPTION_IS_GONE);
                refreshSubscription();
            } else if (status === 502 || status === 503) {
                reportFailure(PROVIDER_UNREACHABLE);
            } else {
                reportFailure(UPDATE_FAILED);
            }
        } finally {
            setUpdatedPlanCode('');
        }
    };

    return {
        updateSubscription,
        updatedPlanCode,
        isUpdatingSubscription,
    };
}
